/**
 * LA GUARDA DE SALIDA DEL CUADERNO (CQ4a · PLAN §3): el test anti-alucinación.
 *
 * Antes de generar el PDF o el Excel, TODO número que se va a imprimir se vuelve
 * a comprobar contra las filas de Pest, sin fiarse de quien montó el documento
 * (el motor, un retoque posterior, o mañana el asistente):
 *
 *   1. todo nº de registro impreso EXISTE en el catálogo del MAPA —salvo que la
 *      empresa tenga el MAPA apagado (D26), o que el catálogo no haya llegado
 *      (L7: entonces no se puede comprobar, NO se bloquea; el motor lo deja como
 *      hueco «sin comprobar», D23)— y sale de las líneas de esa fila (el
 *      resolvedor único, REG0, lo da para alguna de ellas): un nº que no viene de
 *      ningún dato es un nº inventado;
 *   2. toda dosis impresa coincide con la que calcula el código (`dosisPorHa`,
 *      T1) para CADA línea de su fila;
 *   3. toda superficie impresa coincide con la suma de las áreas de sus partes,
 *      con la misma regla que el motor;
 *   4. ninguna línea que se ha quedado FUERA por el borrador tiene un indicio de
 *      fitosanitario (A2 de la revisión de la ola 3): se vuelve a pasar cada una
 *      por la lista blanca de D16 (`motivoSeQueda`, la regla de la RPC) con el
 *      resolvedor y el catálogo; solo un abono o un auxiliar puede faltar del 3.1.
 *
 * Si algo no cuadra NO se genera, y se dice qué y dónde. Un hueco (dosis o nº
 * vacíos) no para nada: se descarga con huecos y la hoja de revisión (D23).
 *
 * LO QUE DECIDIÓ LA PERSONA NO SE BLOQUEA (CU9, decisión de Pablo del 28-09-2026:
 * «el cuaderno de campo es SU documento»). En una fila con campos puestos por la
 * persona (`meta.usuario`) o añadida por ella (`meta.anadida`) la guarda NO vuelve a
 * calcular esos campos: la dosis, la superficie, el producto y el nº se imprimen tal
 * cual. Lo que ve lo apunta como AVISO (`avisos`, se enseña en la tarjeta y la hoja
 * de revisión) y el cuaderno se genera igual:
 *   · un nº dado que no existe en el MAPA, o que está cancelado;
 *   · una dosis o una superficie distintas de las que salen del parte;
 *   · una fila que no viene de ningún parte;
 *   · un fitosanitario quitado del documento (antes, regla 4: bloqueaba).
 * (La dosis máxima de la etiqueta no está en la copia del MAPA: «supera la etiqueta»
 * no se puede comprobar todavía; queda la diferencia con el parte.)
 * Lo que NO decidió nadie (lo que sale del motor) sigue con las reglas 1–3 de arriba:
 * un número que no viene de ningún dato sigue siendo un número inventado.
 *
 * PURO: sin red.
 */
import { buildRegistryIndex, registryKey, resolveRegistration } from "@/lib/registry/resolveRegistration";
import { dosisPorHa } from "./dosePerHa";
import type { EntradaCuaderno } from "./entrada";
import { casaConRegistroPorNombre, indiceNombresRegistro, motivoSeQueda, type MotivoSeQueda } from "./borrador";
import type { MetaFila31 } from "./rows31";
import type { CuadernoDataInput } from "./types";
import { dosis as fmtDosis, hectareas } from "./format";

export type MotivoGuarda =
  | { tipo: "filas_desalineadas"; filas: number; meta: number }
  | { tipo: "registro_inexistente"; fila: number; producto: string; numero: string }
  | { tipo: "registro_no_casa"; fila: number; producto: string; numero: string }
  | { tipo: "dosis_no_cuadra"; fila: number; producto: string; impresa: string; calculada: string }
  | { tipo: "superficie_no_cuadra"; fila: number; producto: string; impresa: string; calculada: string }
  // Regla 4 (A2): una línea fuera del 3.1 por el borrador que es (o puede ser) un fitosanitario.
  | { tipo: "fitosanitario_fuera"; producto: string; linea: string; motivo: MotivoSeQueda };

/** CU9: lo que la persona decidió y conviene que vea antes de firmar. NO bloquea. */
export type AvisoGuarda =
  | { tipo: "registro_usuario_inexistente"; fila: number; producto: string; numero: string }
  | { tipo: "registro_usuario_cancelado"; fila: number; producto: string; numero: string }
  | { tipo: "dosis_distinta_del_parte"; fila: number; producto: string; impresa: string; delParte: string }
  | { tipo: "superficie_distinta_del_parte"; fila: number; producto: string; impresa: string; delParte: string }
  | { tipo: "linea_sin_parte"; fila: number; producto: string }
  | { tipo: "fitosanitario_quitado"; producto: string; linea: string; motivo: MotivoSeQueda };

export interface ResultadoGuarda {
  /** true = se puede generar. Los avisos no lo cambian. */
  ok: boolean;
  motivos: MotivoGuarda[];
  avisos: AvisoGuarda[];
}

const EPS = 1e-9;
const r4 = (n: number) => Math.round(n * 10000) / 10000;

export function guardaDeSalida(args: {
  data: CuadernoDataInput;
  meta31: MetaFila31[];
  entrada: EntradaCuaderno;
  /** Las líneas que el cuaderno dejó fuera (`build.fuera`): la regla 4 mira las «excluida». */
  fuera?: { linea: string; motivo: string }[];
}): ResultadoGuarda {
  const { data, meta31, entrada } = args;
  const motivos: MotivoGuarda[] = [];
  const avisos: AvisoGuarda[] = [];
  const filas = data["3.1"] ?? [];
  if (filas.length !== meta31.length) {
    return { ok: false, motivos: [{ tipo: "filas_desalineadas", filas: filas.length, meta: meta31.length }], avisos };
  }

  const mapa = entrada.mapaValidation !== false;
  const catalogo = entrada.catalogo ?? [];
  const indice = catalogo.length ? buildRegistryIndex(catalogo) : null;
  const lineas = new Map(entrada.lineas.map((l) => [l.id, l]));
  const partes = new Map(entrada.partes.map((p) => [p.id, p]));
  const sectores = new Map(entrada.sectores.map((s) => [s.id, s]));
  const inventario = new Map((entrada.inventario ?? []).map((i) => [i.id, i]));

  const areaDeParte = (parteId: string): number | null => {
    const p = partes.get(parteId);
    if (!p) return null;
    const a = Number(p.areaM2);
    if (Number.isFinite(a) && a > 0) return a;
    const b = Number(p.blockId ? sectores.get(p.blockId)?.areaM2 : NaN);
    return Number.isFinite(b) && b > 0 ? b : null;
  };

  const porNum = new Map(catalogo.map((it) => [registryKey(it.num) ?? it.num, it]));
  /** La dosis que sale del PARTE para una línea (la cuenta del motor). */
  const dosisDelParte = (l: NonNullable<ReturnType<typeof lineas.get>>) => {
    const inv = l.productoId ? inventario.get(l.productoId) : undefined;
    return dosisPorHa({
      cantidad: l.cantidad,
      unidad: l.unidad,
      dosisValor: l.dosisValor,
      dosisUnidad: l.dosisUnidad,
      agua: l.agua,
      aguaUnidad: l.aguaUnidad,
      unidadStock: inv?.unidad ?? l.unidad,
      areaM2: areaDeParte(l.parteId),
    });
  };

  filas.forEach((fila, i) => {
    const meta = meta31[i];
    const producto = fila.producto?.nombreComercial ?? "—";
    const suyas = meta.lineas.map((id) => lineas.get(id)).filter(Boolean);
    // CU9: lo que decidió la persona en esta fila (no se recalcula: se avisa).
    const usuario = new Set<string>(meta.usuario ?? []);
    if (meta.anadida) {
      avisos.push({ tipo: "linea_sin_parte", fila: i, producto });
      usuario.add("producto");
    }
    const deParte = suyas.filter((l) => !l.anadida);

    // ── 1. Nº de registro ──────────────────────────────────────────────────
    const numero = (fila.producto?.numeroRegistro ?? "").trim();
    if (numero && usuario.has("producto")) {
      // El nº lo dio la persona: se imprime tal cual; si el MAPA no lo tiene, o cancelado, se avisa.
      if (mapa && indice) {
        const it = porNum.get(registryKey(numero) ?? numero);
        if (!it || !indice.has(registryKey(numero) ?? "")) avisos.push({ tipo: "registro_usuario_inexistente", fila: i, producto, numero });
        else if (it.vigente === false) avisos.push({ tipo: "registro_usuario_cancelado", fila: i, producto, numero });
      }
    } else if (numero) {
      // Sin catálogo (L7) no se mira si existe: solo que salga de sus líneas (abajo).
      if (mapa && indice && !indice.has(registryKey(numero) ?? "")) {
        motivos.push({ tipo: "registro_inexistente", fila: i, producto, numero });
      } else {
        const k = registryKey(numero) ?? numero;
        const deSusLineas = suyas.some((l) => {
          const inv = l.productoId ? inventario.get(l.productoId) : undefined;
          const res = resolveRegistration(
            { snapshot: l.registro, inventory: inv?.registro, productName: l.nombre, batch: l.lote },
            { catalog: indice, mapaValidation: entrada.mapaValidation },
          );
          return res.value != null && (registryKey(res.value) ?? res.value) === k;
        });
        if (!deSusLineas) motivos.push({ tipo: "registro_no_casa", fila: i, producto, numero });
      }
    }

    // ── 2. Dosis por hectárea ──────────────────────────────────────────────
    if (fila.dosis && (usuario.has("dosis") || usuario.has("cantidad"))) {
      // La dio la persona: tal cual. Si el parte dice otra, se avisa (el parte no cambia).
      const l = deParte[0];
      const calc = l ? dosisDelParte(l) : null;
      if (calc && calc.ok && "valor" in calc && (Math.abs(calc.valor - fila.dosis.valor) >= EPS || calc.unidad !== fila.dosis.unidad)) {
        avisos.push({ tipo: "dosis_distinta_del_parte", fila: i, producto, impresa: fmtDosis(fila.dosis), delParte: fmtDosis({ valor: calc.valor, unidad: calc.unidad }) });
      }
    } else if (fila.dosis) {
      const impresa = fmtDosis(fila.dosis);
      for (const l of suyas) {
        const inv = l.productoId ? inventario.get(l.productoId) : undefined;
        const calc = dosisPorHa({
          cantidad: l.cantidad,
          unidad: l.unidad,
          dosisValor: l.dosisValor,
          dosisUnidad: l.dosisUnidad,
          agua: l.agua,
          aguaUnidad: l.aguaUnidad,
          unidadStock: inv?.unidad ?? l.unidad,
          areaM2: areaDeParte(l.parteId),
        });
        const cuadra = calc.ok && "valor" in calc && Math.abs(calc.valor - fila.dosis.valor) < EPS && calc.unidad === fila.dosis.unidad;
        if (!cuadra) {
          motivos.push({
            tipo: "dosis_no_cuadra",
            fila: i,
            producto,
            impresa,
            calculada: calc.ok && "valor" in calc ? fmtDosis({ valor: calc.valor, unidad: calc.unidad }) : "—",
          });
          break;
        }
      }
    }

    // ── 3. Superficie tratada (misma regla que el motor: suma por línea) ──
    if (fila.superficieHa != null && usuario.has("superficie")) {
      const areas = deParte.map((l) => areaDeParte(l.parteId));
      const delParte = areas.length && areas.every((a) => a != null) ? r4(areas.reduce((s, a) => s + (a as number), 0) / 10000) : null;
      if (delParte != null && Math.abs(delParte - fila.superficieHa) > EPS) {
        avisos.push({ tipo: "superficie_distinta_del_parte", fila: i, producto, impresa: hectareas(fila.superficieHa), delParte: hectareas(delParte) });
      }
    } else if (fila.superficieHa != null) {
      const areas = suyas.map((l) => areaDeParte(l.parteId));
      const calculada = areas.length && areas.every((a) => a != null) ? r4(areas.reduce((s, a) => s + (a as number), 0) / 10000) : null;
      if (calculada == null || Math.abs(calculada - fila.superficieHa) > EPS) {
        motivos.push({
          tipo: "superficie_no_cuadra",
          fila: i,
          producto,
          impresa: hectareas(fila.superficieHa),
          calculada: calculada == null ? "—" : hectareas(calculada),
        });
      }
    }
  });

  // ── 4. Un fitosanitario fuera del 3.1: desde CU9 lo decide la persona → AVISO (antes, A2: bloqueaba) ──
  const nombresMapa = mapa ? indiceNombresRegistro(catalogo) : null;
  for (const f of args.fuera ?? []) {
    if (f.motivo !== "excluida") continue;
    const l = lineas.get(f.linea);
    if (!l) continue;
    const inv = l.productoId ? inventario.get(l.productoId) : undefined;
    const res = resolveRegistration(
      { snapshot: l.registro, inventory: inv?.registro, productName: l.nombre, batch: l.lote },
      { catalog: indice, mapaValidation: entrada.mapaValidation },
    );
    const motivo = motivoSeQueda({
      registroValido: res.value != null,
      categoriaLinea: l.categoria ?? inv?.categoria,
      categoriaProducto: inv?.categoria,
      nombre: l.nombre,
      casaPorNombre: casaConRegistroPorNombre(l.nombre, nombresMapa),
    });
    if (motivo) avisos.push({ tipo: "fitosanitario_quitado", producto: l.nombre, linea: l.id, motivo });
  }

  return { ok: motivos.length === 0, motivos, avisos };
}
